// src/pages/motolivraison/commerces.page.jsx
//
// Liste de tous les commerces du module MotoLivraison avec filtres et actions admin :
// validation, suspension, réjection, réactivation.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, message, Button, Card, Row, Col, Tag, Tooltip, Space,
  Breadcrumb, Avatar, Select, Form, Dropdown, DatePicker
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import {
  CheckCircleOutlined, StopOutlined, EyeOutlined, PlayCircleOutlined,
  ShopOutlined, RightOutlined, SearchOutlined, DownOutlined, CloseCircleOutlined,
  ClockCircleOutlined, UserAddOutlined, EnvironmentOutlined,
  DownloadOutlined, FileExcelOutlined, FilePdfOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { motolivraisonService, citiesService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import { CellTooltip } from './table.helpers';
import './css/motolivraison-table.css';

dayjs.locale('fr');

const { RangePicker } = DatePicker;

const Commerces = () => {
  const [commerces, setCommerces] = useState([]);
  const [loading, setLoading] = useState(false);
  // Filtres envoyés au serveur : recherche texte + période d'inscription (bornes incluses).
  // Ville / catégorie / statut sont filtrés directement sur les colonnes du tableau.
  const [filters, setFilters] = useState({ q: '', dateDebut: '', dateFin: '' });
  // Filtres et tri de colonnes (contrôlés pour pouvoir les réinitialiser)
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({ columnKey: 'createdAt', order: 'descend' });
  const [cities, setCities] = useState([]);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [cityForm] = Form.useForm();
  const [savingCity, setSavingCity] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedCommerce, setSelectedCommerce] = useState(null);
  const [rejectForm] = Form.useForm();
  const [createCommercantModalOpen, setCreateCommercantModalOpen] = useState(false);
  const [createCommercantForm] = Form.useForm();
  const [creatingCommercant, setCreatingCommercant] = useState(false);
  const navigate = useNavigate();
  // Arrivée depuis une fiche catégorie : /commerces?categorieCode=restauration
  const [searchParams, setSearchParams] = useSearchParams();
  const categorieCode = searchParams.get('categorieCode') || '';

  const stats = {
    total: commerces.length,
    pending: commerces.filter(c => c.status === 'pending').length,
    approved: commerces.filter(c => c.status === 'approved').length,
    suspended: commerces.filter(c => c.status === 'suspended').length,
  };

  const fetchCommerces = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommerces({
        q: filters.q || undefined,
        dateDebut: filters.dateDebut || undefined,
        dateFin: filters.dateFin || undefined,
        categorieCode: categorieCode || undefined,
        // Le tri et les filtres de colonnes travaillent sur le jeu complet, pas sur
        // la première page renvoyée par le serveur (défaut : 20).
        limit: 1000,
      });
      setCommerces(data?.commerces || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commerces');
    } finally {
      setLoading(false);
    }
  }, [filters, categorieCode]);

  useEffect(() => {
    fetchCommerces();
  }, [fetchCommerces]);

  // Villes actives — filtre de liste, correction de ville et création de commerçant
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res) ? res : (res?.data || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const cityOptions = cities.map(c => ({ value: c.code, label: c.name }));

  const handleAction = async (action, commerce) => {
    try {
      const labels = {
        validate: 'approuvé',
        suspend: 'suspendu',
        reactivate: 'réactivé',
      };
      if (action === 'validate') await motolivraisonService.validateCommerce(commerce._id);
      else if (action === 'suspend') await motolivraisonService.suspendCommerce(commerce._id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommerce(commerce._id);
      message.success(`Commerce ${labels[action]}`);
      fetchCommerces();
    } catch (e) {
      message.error(e?.message || 'Erreur lors de l\'action');
    }
  };

  const openReject = (commerce) => {
    setSelectedCommerce(commerce);
    rejectForm.resetFields();
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    try {
      const { reason } = await rejectForm.validateFields();
      await motolivraisonService.rejectCommerce(selectedCommerce._id, reason);
      message.success('Commerce rejeté');
      setRejectModalOpen(false);
      fetchCommerces();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors du rejet');
    }
  };

  // ─── Correction de la ville d'un commerce ──────────────────────────────
  const openChangeCity = (commerce) => {
    setSelectedCommerce(commerce);
    cityForm.setFieldsValue({ cityCode: commerce?.cityId?.code });
    setCityModalOpen(true);
  };

  const handleChangeCity = async () => {
    try {
      const { cityCode } = await cityForm.validateFields();
      setSavingCity(true);
      await motolivraisonService.updateCommerceCity(selectedCommerce._id, cityCode);
      message.success('Ville du commerce mise à jour');
      setCityModalOpen(false);
      fetchCommerces();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors du changement de ville');
    } finally {
      setSavingCity(false);
    }
  };

  const openCreateCommercant = () => {
    createCommercantForm.resetFields();
    setCreateCommercantModalOpen(true);
  };

  const handleCreateCommercant = async () => {
    try {
      const values = await createCommercantForm.validateFields();
      setCreatingCommercant(true);
      const res = await motolivraisonService.createCommercantByAdmin(values);
      const successMsg = res?.userCreated
        ? `Commerçant créé. Mot de passe par défaut : 123456`
        : (res?.profileAlreadyExists
            ? 'Cet utilisateur est déjà commerçant approuvé'
            : 'Profil commerçant ajouté à un utilisateur existant');
      message.success(successMsg);
      setCreateCommercantModalOpen(false);
      fetchCommerces();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la création');
    } finally {
      setCreatingCommercant(false);
    }
  };

  const statusColor = (s) => ({
    pending: 'orange',
    approved: 'green',
    suspended: 'red',
    rejected: 'default',
  })[s] || 'default';

  const statusLabel = (s) => ({
    pending: 'En attente',
    approved: 'Approuvé',
    suspended: 'Suspendu',
    rejected: 'Rejeté',
  })[s] || s;

  // Options de filtre de colonnes, construites sur les données réellement chargées
  const uniqueOptions = (valeurs) =>
    [...new Set(valeurs.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'))
      .map(v => ({ text: v, value: v }));

  const categorieFilters = uniqueOptions(commerces.map(c => c.categorieId?.nom));
  const villeFilters = uniqueOptions(commerces.map(c => c.cityId?.name));
  const quartierFilters = uniqueOptions(commerces.map(c => c.quartier));
  const statutFilters = ['pending', 'approved', 'suspended', 'rejected']
    .map(s => ({ text: statusLabel(s), value: s }));

  // Ordre de tri courant d'une colonne (null si ce n'est pas elle qui trie)
  const ordreDe = (key) => (sortedInfo?.columnKey === key ? sortedInfo.order : null);

  // Comparateur texte tolérant aux valeurs vides
  const cmpTexte = (a, b) => String(a || '').localeCompare(String(b || ''), 'fr');

  const aDesFiltresColonnes = Object.values(filteredInfo).some(v => v && v.length);

  // ─── Exports (Excel / PDF) ──────────────────────────────────────────────
  // On exporte EXACTEMENT ce que l'utilisateur voit : mêmes filtres de colonnes
  // et même tri que le tableau (la recherche et la période sont déjà appliquées
  // côté serveur sur `commerces`).
  const lignesExportables = () => {
    const f = filteredInfo || {};
    let rows = commerces.filter(r =>
      (!f.categorie?.length || f.categorie.includes(r.categorieId?.nom)) &&
      (!f.quartier?.length || f.quartier.includes(r.quartier)) &&
      (!f.ville?.length || f.ville.includes(r.cityId?.name)) &&
      (!f.status?.length || f.status.includes(r.status))
    );

    const { columnKey, order } = sortedInfo || {};
    const valeurs = {
      nom: r => r.nom,
      categorie: r => r.categorieId?.nom,
      proprietaire: r => `${r.ownerId?.firstName || ''} ${r.ownerId?.lastName || ''}`,
      adresse: r => r.adresse,
      quartier: r => r.quartier,
      ville: r => r.cityId?.name,
      status: r => statusLabel(r.status),
      createdAt: r => new Date(r.createdAt || 0).getTime(),
    };
    if (columnKey && order && valeurs[columnKey]) {
      const lire = valeurs[columnKey];
      rows = [...rows].sort((a, b) => {
        const va = lire(a); const vb = lire(b);
        const cmp = typeof va === 'number' ? va - vb : cmpTexte(va, vb);
        return order === 'descend' ? -cmp : cmp;
      });
    }
    return rows;
  };

  // Colonnes du fichier exporté (le logo n'a pas de sens hors écran)
  const ENTETES_EXPORT = ['Commerce', 'Catégorie', 'Propriétaire', 'Téléphone', 'Adresse', 'Quartier', 'Ville', 'Statut', 'Inscrit le'];
  const ligneExport = (r) => [
    r.nom || '',
    r.categorieId?.nom || '',
    r.ownerId ? `${r.ownerId.firstName || ''} ${r.ownerId.lastName || ''}`.trim() : '',
    formatPhoneCI(r.ownerId?.phone || r.telephone, ''),
    r.adresse || '',
    r.quartier || '',
    r.cityId?.name || '',
    statusLabel(r.status),
    r.createdAt ? dayjs(r.createdAt).format('DD/MM/YYYY') : '',
  ];

  // Rappel des filtres actifs, imprimé dans le PDF et en tête du fichier Excel
  const resumeFiltres = () => {
    const parts = [];
    if (filters.q) parts.push(`Recherche : « ${filters.q} »`);
    if (filters.dateDebut || filters.dateFin) {
      parts.push(`Période : ${filters.dateDebut ? dayjs(filters.dateDebut).format('DD/MM/YYYY') : '…'} → ${filters.dateFin ? dayjs(filters.dateFin).format('DD/MM/YYYY') : '…'}`);
    }
    if (filteredInfo.ville?.length) parts.push(`Ville : ${filteredInfo.ville.join(', ')}`);
    if (filteredInfo.categorie?.length) parts.push(`Catégorie : ${filteredInfo.categorie.join(', ')}`);
    if (filteredInfo.quartier?.length) parts.push(`Quartier : ${filteredInfo.quartier.join(', ')}`);
    if (filteredInfo.status?.length) parts.push(`Statut : ${filteredInfo.status.map(statusLabel).join(', ')}`);
    return parts.join('  |  ');
  };

  const nomFichier = (ext) => `commerces_motolivraison_${dayjs().format('YYYY-MM-DD_HHmm')}.${ext}`;

  const exporterExcel = () => {
    const rows = lignesExportables();
    if (!rows.length) return message.warning('Aucun commerce à exporter');

    const donnees = rows.map(r => Object.fromEntries(ENTETES_EXPORT.map((h, i) => [h, ligneExport(r)[i]])));
    const ws = XLSX.utils.json_to_sheet(donnees, { header: ENTETES_EXPORT });
    ws['!cols'] = [{ wch: 28 }, { wch: 22 }, { wch: 26 }, { wch: 16 }, { wch: 26 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commerces');
    XLSX.writeFile(wb, nomFichier('xlsx'));
    message.success(`${rows.length} commerce${rows.length > 1 ? 's' : ''} exporté${rows.length > 1 ? 's' : ''} vers Excel`);
  };

  const exporterPdf = () => {
    const rows = lignesExportables();
    if (!rows.length) return message.warning('Aucun commerce à exporter');

    // Paysage : 9 colonnes ne tiennent pas en portrait.
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Commerces MotoLivraison', 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Généré le ${dayjs().format('DD/MM/YYYY à HH:mm')} — ${rows.length} commerce${rows.length > 1 ? 's' : ''}`, 14, 22);
    const resume = resumeFiltres();
    if (resume) doc.text(resume, 14, 27);

    autoTable(doc, {
      startY: resume ? 32 : 28,
      head: [ENTETES_EXPORT],
      body: rows.map(ligneExport),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [51, 65, 85], textColor: 248 }, // ardoise, comme le tableau
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(nomFichier('pdf'));
    message.success(`${rows.length} commerce${rows.length > 1 ? 's' : ''} exporté${rows.length > 1 ? 's' : ''} vers PDF`);
  };

  const menuExport = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#217346' }} />, label: 'Exporter en Excel (.xlsx)', onClick: exporterExcel },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#d32029' }} />, label: 'Exporter en PDF', onClick: exporterPdf },
  ];

  const reinitialiserFiltres = () => {
    setFilters({ q: '', dateDebut: '', dateFin: '' });
    setSearchParams({});
    setFilteredInfo({});
    setSortedInfo({ columnKey: 'createdAt', order: 'descend' });
  };

  // Une information = une colonne (logo, nom, catégorie, propriétaire, téléphone,
  // adresse, quartier, ville, statut, date), la colonne Actions restant figée à droite.
  const columns = [
    {
      title: 'Logo',
      key: 'logo',
      width: 80,
      align: 'center',
      render: (_, r) => (
        <Avatar size={40} src={r.logo} icon={<ShopOutlined />} style={{ backgroundColor: '#fa8c16' }} />
      ),
    },
    {
      title: 'Commerce',
      dataIndex: 'nom',
      key: 'nom',
      width: 180,
      ellipsis: { showTitle: false },
      sorter: (a, b) => cmpTexte(a.nom, b.nom),
      sortOrder: ordreDe('nom'),
      render: (v, r) => (
        <CellTooltip label="Commerce" value={v} extra={r.categorieId?.nom}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{v}</span>
        </CellTooltip>
      ),
    },
    {
      title: 'Catégorie',
      key: 'categorie',
      width: 160,
      ellipsis: { showTitle: false },
      filters: categorieFilters,
      filteredValue: filteredInfo.categorie || null,
      onFilter: (value, r) => r.categorieId?.nom === value,
      sorter: (a, b) => cmpTexte(a.categorieId?.nom, b.categorieId?.nom),
      sortOrder: ordreDe('categorie'),
      render: (_, r) => (
        <CellTooltip label="Catégorie" value={r.categorieId?.nom}>
          <span>{r.categorieId?.nom || '—'}</span>
        </CellTooltip>
      ),
    },
    {
      title: 'Propriétaire',
      key: 'proprietaire',
      width: 200,
      ellipsis: { showTitle: false },
      sorter: (a, b) => cmpTexte(
        `${a.ownerId?.firstName || ''} ${a.ownerId?.lastName || ''}`,
        `${b.ownerId?.firstName || ''} ${b.ownerId?.lastName || ''}`,
      ),
      sortOrder: ordreDe('proprietaire'),
      render: (_, r) => {
        const nom = r.ownerId
          ? `${r.ownerId.firstName || ''} ${r.ownerId.lastName || ''}`.trim()
          : '';
        return (
          <CellTooltip
            label="Propriétaire"
            value={nom}
            extra={r.ownerId?.phone ? formatPhoneCI(r.ownerId.phone) : null}
          >
            <span>{nom || '—'}</span>
          </CellTooltip>
        );
      },
    },
    {
      title: 'Téléphone',
      key: 'telephone',
      width: 150,
      render: (_, r) => {
        const brut = r.ownerId?.phone || r.telephone;
        const label = r.ownerId?.phone ? 'Téléphone du propriétaire' : 'Téléphone de la boutique';
        return (
          <CellTooltip label={label} value={formatPhoneCI(brut, '')}>
            <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneCI(brut)}</span>
          </CellTooltip>
        );
      },
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      key: 'adresse',
      width: 180,
      ellipsis: { showTitle: false },
      sorter: (a, b) => cmpTexte(a.adresse, b.adresse),
      sortOrder: ordreDe('adresse'),
      render: (v, r) => (
        <CellTooltip label="Adresse" value={v} extra={[r.quartier, r.cityId?.name].filter(Boolean).join(' · ')}>
          <span>{v || '—'}</span>
        </CellTooltip>
      ),
    },
    {
      title: 'Quartier',
      dataIndex: 'quartier',
      key: 'quartier',
      width: 160,
      ellipsis: { showTitle: false },
      filters: quartierFilters,
      filteredValue: filteredInfo.quartier || null,
      filterSearch: true,
      onFilter: (value, r) => r.quartier === value,
      sorter: (a, b) => cmpTexte(a.quartier, b.quartier),
      sortOrder: ordreDe('quartier'),
      render: (v, r) => (
        <CellTooltip label="Quartier" value={v} extra={r.cityId?.name}>
          <span>{v || '—'}</span>
        </CellTooltip>
      ),
    },
    {
      title: 'Ville',
      dataIndex: 'cityId',
      key: 'ville',
      width: 130,
      filters: villeFilters,
      filteredValue: filteredInfo.ville || null,
      onFilter: (value, r) => r.cityId?.name === value,
      sorter: (a, b) => cmpTexte(a.cityId?.name, b.cityId?.name),
      sortOrder: ordreDe('ville'),
      render: (c) => c?.name || '—',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      filters: statutFilters,
      filteredValue: filteredInfo.status || null,
      onFilter: (value, r) => r.status === value,
      sorter: (a, b) => cmpTexte(statusLabel(a.status), statusLabel(b.status)),
      sortOrder: ordreDe('status'),
      render: (s) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    {
      title: 'Inscrit le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      sortOrder: ordreDe('createdAt'),
      render: (v) => v ? (
        <CellTooltip label="Inscrit le" value={dayjs(v).format('dddd D MMMM YYYY à HH:mm')}>
          <span style={{ whiteSpace: 'nowrap' }}>{dayjs(v).format('DD/MM/YYYY')}</span>
        </CellTooltip>
      ) : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 210,
      align: 'center',
      fixed: 'right',
      render: (_, r) => {
        const items = [{
          key: 'city',
          icon: <EnvironmentOutlined style={{ color: '#1677ff' }} />,
          label: 'Changer la ville',
          onClick: () => openChangeCity(r),
        }];
        if (r.status === 'pending') {
          items.push({
            key: 'validate',
            icon: <CheckCircleOutlined style={{ color: 'green' }} />,
            label: 'Approuver',
            onClick: () => handleAction('validate', r),
          });
          items.push({
            key: 'reject',
            icon: <CloseCircleOutlined style={{ color: 'red' }} />,
            label: 'Rejeter',
            onClick: () => openReject(r),
          });
        }
        if (r.status === 'approved') {
          items.push({
            key: 'suspend',
            icon: <StopOutlined style={{ color: 'red' }} />,
            label: 'Suspendre',
            onClick: () => handleAction('suspend', r),
          });
        }
        if (r.status === 'suspended') {
          items.push({
            key: 'reactivate',
            icon: <PlayCircleOutlined style={{ color: 'green' }} />,
            label: 'Réactiver',
            onClick: () => handleAction('reactivate', r),
          });
        }
        return (
          <Space>
            <Tooltip title="Ouvrir la fiche du commerce" mouseEnterDelay={0.35} color="#1f1f1f">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/dashboard/motolivraison/commerces/${r._id}`)}
                style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
              >
                Détails
              </Button>
            </Tooltip>
            {items.length > 0 && (
              <Dropdown menu={{ items }}>
                <Button size="small">
                  Actions <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12 }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commerces</Breadcrumb.Item>
        </Breadcrumb>

        {/* Cartes compactes : elles ne doivent pas manger la place du tableau. */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {[
            { label: 'Total', value: stats.total, icon: <ShopOutlined />, color: '#595959' },
            { label: 'En attente', value: stats.pending, icon: <ClockCircleOutlined />, color: '#fa8c16' },
            { label: 'Approuvés', value: stats.approved, icon: <CheckCircleOutlined />, color: '#52c41a' },
            { label: 'Suspendus', value: stats.suspended, icon: <StopOutlined />, color: '#f5222d' },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.label}>
              <Card size="small" styles={{ body: { padding: '10px 14px' } }}>
                <Space align="center" size={10}>
                  <span style={{ fontSize: 18, color: s.color, display: 'flex' }}>{s.icon}</span>
                  <div style={{ lineHeight: 1.2 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: s.color }}>{s.value}</div>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          title="Commerces MotoLivraison"
          extra={
            <Space>
              <Dropdown menu={{ items: menuExport }} disabled={!commerces.length}>
                <Button icon={<DownloadOutlined />}>
                  Exporter <DownOutlined />
                </Button>
              </Dropdown>
              <Button type="primary" icon={<UserAddOutlined />} onClick={openCreateCommercant}>
                Nouveau commerçant
              </Button>
            </Space>
          }
        >
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Rechercher un commerce..."
                allowClear
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </Col>
            <Col xs={24} sm={16}>
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                allowEmpty={[true, true]}
                placeholder={['Date de début', 'Date de fin']}
                value={[
                  filters.dateDebut ? dayjs(filters.dateDebut) : null,
                  filters.dateFin ? dayjs(filters.dateFin) : null,
                ]}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                onChange={(dates) => setFilters({
                  ...filters,
                  dateDebut: dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
                  dateFin: dates?.[1] ? dates[1].format('YYYY-MM-DD') : '',
                })}
                presets={[
                  { label: "Aujourd'hui", value: [dayjs(), dayjs()] },
                  { label: '7 derniers jours', value: [dayjs().subtract(6, 'day'), dayjs()] },
                  { label: '30 derniers jours', value: [dayjs().subtract(29, 'day'), dayjs()] },
                  { label: 'Ce mois-ci', value: [dayjs().startOf('month'), dayjs()] },
                ]}
              />
            </Col>
          </Row>

          {(filters.q || filters.dateDebut || filters.dateFin || aDesFiltresColonnes || categorieCode) && (
            <div style={{ marginBottom: 12 }}>
              <Space wrap size={8}>
                {categorieCode && (
                  <Tag closable color="orange" onClose={() => setSearchParams({})}>
                    Catégorie : {commerces[0]?.categorieId?.nom || categorieCode}
                  </Tag>
                )}
                <Button size="small" onClick={reinitialiserFiltres}>
                  Réinitialiser les filtres et le tri
                </Button>
              </Space>
            </div>
          )}

          <Table
            className="moto-table"
            columns={columns}
            dataSource={commerces.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            size="middle"
            // Libellés en français (AntD est en anglais par défaut sur ce projet)
            locale={{
              triggerDesc: 'Trier par ordre décroissant',
              triggerAsc: 'Trier par ordre croissant',
              cancelSort: 'Annuler le tri',
              filterTitle: 'Filtrer',
              filterConfirm: 'Appliquer',
              filterReset: 'Réinitialiser',
              filterSearchPlaceholder: 'Rechercher',
              emptyText: 'Aucun commerce',
              selectAll: 'Tout sélectionner',
            }}
            // Filtres et tri de colonnes (AntD renvoie l'état complet à chaque changement)
            onChange={(pagination, colFilters, sorter) => {
              setFilteredInfo(colFilters);
              setSortedInfo(Array.isArray(sorter) ? sorter[0] : sorter);
            }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, [debut, fin]) => `${debut}-${fin} sur ${total} commerce${total > 1 ? 's' : ''}`,
            }}
            // Somme des largeurs de colonnes → défilement horizontal, Actions figée à droite.
            scroll={{ x: 1750 }}
          />
        </Card>
      </div>

      <Modal
        title="Rejeter le commerce"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        okText="Confirmer le rejet"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Raison du rejet"
            rules={[{ required: true, message: 'La raison est requise' }]}
          >
            <Input.TextArea rows={3} placeholder="Expliquez pourquoi ce commerce est rejeté..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Changer la ville */}
      <Modal
        title={
          <span>
            <EnvironmentOutlined style={{ marginRight: 8 }} />
            Ville du commerce
          </span>
        }
        open={cityModalOpen}
        onCancel={() => setCityModalOpen(false)}
        onOk={handleChangeCity}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingCity}
      >
        <p style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 13 }}>
          {selectedCommerce?.nom} — {selectedCommerce?.adresse}
          {selectedCommerce?.quartier ? ` (${selectedCommerce.quartier})` : ''}
        </p>
        <Form form={cityForm} layout="vertical">
          <Form.Item
            name="cityCode"
            label="Ville"
            rules={[{ required: true, message: 'Ville requise' }]}
          >
            <Select placeholder="Choisir la ville" options={cityOptions} showSearch optionFilterProp="label" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Create Commerçant */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8 }} />
            Nouveau commerçant
          </span>
        }
        open={createCommercantModalOpen}
        onCancel={() => setCreateCommercantModalOpen(false)}
        onOk={handleCreateCommercant}
        okText="Créer"
        cancelText="Annuler"
        confirmLoading={creatingCommercant}
        width={560}
      >
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
          }}
        >
          <strong>ℹ️ Mot de passe par défaut :</strong>{' '}
          <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4 }}>123456</code>
          <br />
          <small style={{ color: '#8c8c8c' }}>
            Le commerçant pourra le changer après sa première connexion. Si le numéro existe déjà,
            le profil commerçant sera simplement ajouté à l'utilisateur existant (sans modifier son mot de passe).
          </small>
        </div>
        <Form form={createCommercantForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[{ required: true, message: 'Prénom requis' }]}
              >
                <Input placeholder="Ex: Jean" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Nom">
                <Input placeholder="Ex: Kouassi" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="phone"
            label="Téléphone"
            rules={[{ required: true, message: 'Téléphone requis' }]}
            extra="Format : +225XXXXXXXX"
          >
            <Input placeholder="+225..." />
          </Form.Item>
          <Form.Item name="email" label="Email (optionnel)">
            <Input type="email" placeholder="email@exemple.com" />
          </Form.Item>
          <Form.Item
            name="cityCode"
            label="Ville"
            extra="Ville du commerçant — proposée par défaut pour ses boutiques."
          >
            <Select
              placeholder="Choisir la ville"
              options={cityOptions}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Commerces;
